import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, Box, Card, CardContent, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, LinearProgress, Alert } from '@mui/material';
import { WarningAmberOutlined, SmsFailedOutlined, SignalCellularConnectedNoInternet0BarOutlined } from '@mui/icons-material';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import FilterBar, { EMPTY_FILTERS } from '../components/FilterBar';
import StatCard from '../components/StatCard';
import { getFailedMessages } from '../utils/api';

const TT = { backgroundColor:'#111827', border:'1px solid rgba(255,82,82,0.2)', borderRadius:8 };
const TC = { MTN:'#FFB300', 'Eswatini Mobile':'#00BFA5', Other:'#90A4AE' };

export default function FailedMessagesPage() {
  const [data,    setData]    = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getFailedMessages(filters);
      setData(r.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const monthly = data?.monthly || [];
  const patients = data?.patients || [];

  return (
    <Box>
      <Box mb={2}>
        <Typography variant="h5" fontWeight={700} sx={{ color:'#FF5252' }}>
          <SmsFailedOutlined sx={{ mr:1, verticalAlign:'middle' }}/>
          Failed Messages
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Messages that failed to reach patients — requiring follow-up action
        </Typography>
      </Box>

      <FilterBar filters={filters} onChange={setFilters} showGroup={false} />

      {loading && <LinearProgress sx={{ mb:1, '& .MuiLinearProgress-bar':{ bgcolor:'#FF5252' } }}/>}

      {!loading && parseInt(s.total_failed||0) > 0 && (
        <Alert severity="error" sx={{ mb:2, bgcolor:'rgba(255,82,82,0.08)', border:'1px solid rgba(255,82,82,0.2)' }}>
          <strong>{parseInt(s.total_failed||0)} messages</strong> failed to reach <strong>{parseInt(s.affected_patients||0)} patients</strong>. 
          These patients may have missed critical health information and appointment reminders.
        </Alert>
      )}

      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={3}>
          <StatCard title="Total Failed" value={parseInt(s.total_failed||0).toLocaleString()}
            icon={<SmsFailedOutlined/>} color="#FF5252" loading={loading}/>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="Affected Patients" value={parseInt(s.affected_patients||0).toLocaleString()}
            icon={<WarningAmberOutlined/>} color="#FF7043" loading={loading}/>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="MTN Failed" value={parseInt(s.mtn_failed||0).toLocaleString()}
            icon={<SignalCellularConnectedNoInternet0BarOutlined/>} color="#FFB300" loading={loading}/>
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard title="EM Failed" value={parseInt(s.em_failed||0).toLocaleString()}
            icon={<SignalCellularConnectedNoInternet0BarOutlined/>} color="#00BFA5" loading={loading}/>
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Monthly Failed Messages</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                  <XAxis dataKey="month" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
                  <YAxis tick={{ fontSize:11, fill:'#90A4AE' }}/>
                  <Tooltip contentStyle={TT}/>
                  <Legend/>
                  <Bar dataKey="mtn_failed" name="MTN Failed" fill="#FFB300" radius={[3,3,0,0]}/>
                  <Bar dataKey="em_failed"  name="EM Failed"  fill="#00BFA5" radius={[3,3,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card sx={{ height:'100%' }}>
            <CardContent>
              <Typography variant="h6" mb={2}>Failure Rate by Telecom</Typography>
              {['MTN','Eswatini Mobile'].map(tel => {
                const failed = tel === 'MTN' ? parseInt(s.mtn_failed||0) : parseInt(s.em_failed||0);
                const total  = parseInt(s.total_failed||0);
                const pct    = total > 0 ? ((failed/total)*100).toFixed(1) : 0;
                return (
                  <Box key={tel} mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2" fontWeight={600}>{tel}</Typography>
                      <Typography variant="body2" fontFamily='"IBM Plex Mono",monospace' color={TC[tel]}>
                        {failed} ({pct}%)
                      </Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={parseFloat(pct)}
                      sx={{ height:8, borderRadius:4, bgcolor:'rgba(255,255,255,0.06)',
                        '& .MuiLinearProgress-bar':{ bgcolor: TC[tel], borderRadius:4 } }}/>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>
            Affected Patients — Action Required
          </Typography>
          <TableContainer sx={{ maxHeight:440 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Phone</TableCell>
                  <TableCell>Telecom</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Region</TableCell>
                  <TableCell align="right">Failed</TableCell>
                  <TableCell>First Failure</TableCell>
                  <TableCell>Last Failure</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {patients.map((p, i) => (
                  <TableRow key={i} hover sx={{ '&:hover':{ bgcolor:'rgba(255,82,82,0.04)' } }}>
                    <TableCell sx={{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.75rem' }}>{p.phone}</TableCell>
                    <TableCell>
                      <Chip label={p.telecom} size="small"
                        sx={{ fontSize:'0.62rem', height:20, bgcolor:`${TC[p.telecom]||'#90A4AE'}18`, color:TC[p.telecom]||'#90A4AE' }}/>
                    </TableCell>
                    <TableCell><Typography variant="caption">{p.facility}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{p.region}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" fontFamily='"IBM Plex Mono",monospace'
                        fontWeight={700} color="#FF5252">{p.failed_count}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{p.first_failure}</Typography></TableCell>
                    <TableCell><Typography variant="caption" color="text.secondary">{p.last_failure}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
